// ---------------------------------------------------------------------------
// 2D renderer — top-down orthographic projection of the world onto Canvas2D.
// world x → screen x, world z → screen y. Height (world y) becomes shadow
// offset + scale so lifted objects visibly float.
// ---------------------------------------------------------------------------

import { CARD_THICKNESS, getDef } from "../shared/defs";
import { clamp } from "../shared/math";
import { TABLE, type Player } from "../shared/state";
import { getCounterBadges, getCursorTag, getFace, getHandCursor, getNameTag, HAND_ANCHOR, HAND_SIZE, TAG_SCALE } from "./faces";
import type { RenderObj, Replica } from "./replica";
import type { Renderer } from "./renderer";

export class Render2D implements Renderer {
  readonly mode = "2d" as const;

  constructor(private settings?: import("./settings").Settings) {}

  private canvas!: HTMLCanvasElement;
  private g!: CanvasRenderingContext2D;
  private container: HTMLElement | null = null;
  // Camera: world-units → pixels.
  private cx = 0;
  private cz = 0;
  private scale = 8;
  private time = 0;
  /** Current room's table dims (updated from the replica each frame). */
  private table: { w: number; d: number } = { w: TABLE.w, d: TABLE.d };

  mount(container: HTMLElement): void {
    this.container = container;
    this.canvas = document.createElement("canvas");
    this.canvas.className = "game-canvas";
    container.appendChild(this.canvas);
    this.g = this.canvas.getContext("2d")!;
    this.resize();
  }

  unmount(): void {
    this.canvas.remove();
    this.container = null;
  }

  resize(): void {
    if (!this.container) return;
    const dpr = this.dpr();
    const w = this.container.clientWidth, h = this.container.clientHeight;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    // Fit the table on first layout.
    if (this.scale === 8) {
      this.scale = Math.min(w / (this.table.w + 24), h / (this.table.d + 24));
    }
  }

  focusSeat(seat: number): void {
    this.cx = 0;
    this.cz = 0;
    if (this.container) {
      this.scale = Math.min(
        this.container.clientWidth / (this.table.w + 24),
        this.container.clientHeight / (this.table.d + 24),
      );
    }
  }

  // -------------------------------------------------------------- camera

  private dpr(): number {
    return this.settings?.data.renderScale ?? Math.min(window.devicePixelRatio || 1, 2);
  }

  private toScreen(x: number, z: number): [number, number] {
    const w = this.canvas.width / this.dpr();
    const h = this.canvas.height / this.dpr();
    return [w / 2 + (x - this.cx) * this.scale, h / 2 + (z - this.cz) * this.scale];
  }

  screenToTable(px: number, py: number): { x: number; z: number } {
    const w = this.canvas.width / this.dpr();
    const h = this.canvas.height / this.dpr();
    return {
      x: (px - w / 2) / this.scale + this.cx,
      z: (py - h / 2) / this.scale + this.cz,
    };
  }

  cameraDrag(_kind: "pan" | "orbit", dx: number, dy: number): void {
    // In 2D both gestures pan.
    this.cx -= dx / this.scale;
    this.cz -= dy / this.scale;
  }

  panKeys(dirX: number, dirY: number, dt: number): void {
    // Constant on-screen speed regardless of zoom.
    const k = (900 * dt) / this.scale;
    this.cx += dirX * k;
    this.cz -= dirY * k; // +Y = up-screen = -z in top-down
    this.cx = clamp(this.cx, -this.table.w / 2, this.table.w / 2);
    this.cz = clamp(this.cz, -this.table.d / 2, this.table.d / 2);
  }

  projectToScreen(x: number, z: number, _y?: number): { x: number; y: number } {
    const [sx, sy] = this.toScreen(x, z);
    return { x: sx, y: sy };
  }

  cameraZoom(delta: number, px: number, py: number): void {
    const before = this.screenToTable(px, py);
    this.scale = clamp(this.scale * Math.exp(-delta * 0.0012), 2.5, 110);
    const after = this.screenToTable(px, py);
    this.cx += before.x - after.x;
    this.cz += before.z - after.z;
  }

  // ------------------------------------------------------------- picking

  pick(px: number, py: number, state: Replica): string | null {
    const p = this.screenToTable(px, py);
    // Iterate top-down (reverse draw order).
    const order = state.drawOrder().reverse();
    for (const o of order) {
      const d = getDef(o.def);
      if (d.kind === "zone") continue;
      const hw = d.size[0] / 2, hd = d.size[2] / 2;
      const cos = Math.cos(-o.ryaw), sin = Math.sin(-o.ryaw);
      const dx = p.x - o.rp.x, dz = p.z - o.rp.z;
      const lx = dx * cos - dz * sin, lz = dx * sin + dz * cos;
      if (Math.abs(lx) <= hw && Math.abs(lz) <= hd) return o.id;
    }
    return null;
  }

  // ------------------------------------------------------------ rendering

  render(state: Replica, dt: number, hoveredId: string | null): void {
    this.time += dt;
    // Adopt the room's table size; refit the camera when it changes.
    if (state.table.w !== this.table.w || state.table.d !== this.table.d) {
      this.table = { w: state.table.w, d: state.table.d };
      this.focusSeat(state.seat);
    }
    const dpr = this.dpr();
    const g = this.g;
    const w = this.canvas.width / dpr, h = this.canvas.height / dpr;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Room background.
    g.fillStyle = "#17181c";
    g.fillRect(0, 0, w, h);

    // Table.
    const [tx0, tz0] = this.toScreen(-this.table.w / 2, -this.table.d / 2);
    const tw = this.table.w * this.scale, th = this.table.d * this.scale;
    const rim = 3 * this.scale;
    g.fillStyle = "#4a3423";
    this.rr(tx0 - rim, tz0 - rim, tw + rim * 2, th + rim * 2, 4 * this.scale);
    g.fill();
    const felt = g.createRadialGradient(tx0 + tw / 2, tz0 + th / 2, 10, tx0 + tw / 2, tz0 + th / 2, Math.max(tw, th) * 0.7);
    felt.addColorStop(0, "#2e6b45");
    felt.addColorStop(1, "#245237");
    g.fillStyle = felt;
    this.rr(tx0, tz0, tw, th, 2.5 * this.scale);
    g.fill();

    // Zones.
    for (const o of state.objs.values()) {
      const d = getDef(o.def);
      if (d.kind !== "zone") continue;
      this.drawZone(o, state);
    }

    // Objects, bottom-to-top.
    for (const o of state.drawOrder()) {
      const d = getDef(o.def);
      if (d.kind === "zone") continue;
      this.drawObject(o, o.id === hoveredId, state);
    }

    // Drop preview: ghost footprint at the drop point / highlight target.
    if (state.dropGhost) {
      const gh = state.dropGhost;
      const [gx, gy] = this.toScreen(gh.x, gh.z);
      g.save();
      g.translate(gx, gy);
      g.rotate(gh.yaw);
      g.setLineDash([6, 5]);
      g.strokeStyle = "rgba(255,255,255,0.65)";
      g.lineWidth = 2;
      if (gh.cyl) {
        g.beginPath();
        g.ellipse(0, 0, (gh.w / 2) * this.scale + 3, (gh.w / 2) * this.scale + 3, 0, 0, Math.PI * 2);
        g.stroke();
      } else {
        this.rr(-(gh.w / 2) * this.scale - 3, -(gh.d / 2) * this.scale - 3, gh.w * this.scale + 6, gh.d * this.scale + 6, 0.8 * this.scale);
        g.stroke();
      }
      g.setLineDash([]);
      g.restore();
    }
    if (state.dropTargetId) {
      const t = state.objs.get(state.dropTargetId);
      if (t) {
        const td = getDef(t.def);
        const [tx, ty] = this.toScreen(t.rp.x, t.rp.z);
        g.save();
        g.translate(tx, ty);
        g.rotate(t.ryaw);
        const pulse = 0.55 + 0.25 * Math.sin(this.time * 7);
        g.strokeStyle = `rgba(120,190,255,${pulse})`;
        g.lineWidth = 3;
        if (td.shape === "cylinder") {
          g.beginPath();
          g.ellipse(0, 0, (td.size[0] / 2) * this.scale + 4, (td.size[2] / 2) * this.scale + 4, 0, 0, Math.PI * 2);
          g.stroke();
        } else {
          this.rr(-(td.size[0] / 2) * this.scale - 4, -(td.size[2] / 2) * this.scale - 4, td.size[0] * this.scale + 8, td.size[2] * this.scale + 8, 0.9 * this.scale);
          g.stroke();
        }
        g.restore();
      }
    }

    // Cursors of other players: a little hand + crisp name tag.
    for (const [pid, c] of state.cursors) {
      const p = state.players.find((q) => q.id === pid);
      if (!p) continue;
      const [sx, sy] = this.toScreen(c.rx, c.rz);
      const hand = getHandCursor(p.color);
      const hs = 22; // on-screen size (canvas is oversized for sharpness)
      const anchorX = ((HAND_ANCHOR.x + 2) / 28) * hs;
      const anchorY = ((HAND_ANCHOR.y + 2) / 28) * hs;
      g.drawImage(hand, sx - anchorX, sy - anchorY, hs, hs);
      const tag = getCursorTag(p.name);
      const th = 17;
      g.drawImage(tag, sx + hs * 0.55, sy + hs * 0.85, (tag.width / tag.height) * th, th);
    }
  }

  private rr(x: number, y: number, w: number, h: number, r: number): void {
    const g = this.g;
    r = Math.min(r, w / 2, h / 2);
    g.beginPath();
    g.moveTo(x + r, y);
    g.arcTo(x + w, y, x + w, y + h, r);
    g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r);
    g.arcTo(x, y, x + w, y, r);
    g.closePath();
  }

  private drawZone(o: RenderObj, state: Replica): void {
    const g = this.g;
    const d = getDef(o.def);
    if (o.def === "zone.slot") {
      // Slot marker: subtle dashed box + label (mat art carries most of it).
      const [sx, sy] = this.toScreen(o.rp.x, o.rp.z);
      g.save();
      g.translate(sx, sy);
      g.rotate(o.ryaw);
      const zw2 = d.size[0] * this.scale, zd2 = d.size[2] * this.scale;
      this.rr(-zw2 / 2, -zd2 / 2, zw2, zd2, 0.8 * this.scale);
      g.setLineDash([4, 4]);
      g.strokeStyle = "rgba(255,255,255,0.25)";
      g.lineWidth = 1;
      g.stroke();
      g.setLineDash([]);
      g.restore();
      return;
    }
    const owner: Player | undefined = state.players.find((p) => p.seat === o.seat && p.connected);
    const mine = o.seat === state.seat;
    const [sx, sy] = this.toScreen(o.rp.x, o.rp.z);
    g.save();
    g.translate(sx, sy);
    g.rotate(o.ryaw);
    const zw = (o.zw ?? d.size[0]) * this.scale, zd = d.size[2] * this.scale;
    this.rr(-zw / 2, -zd / 2, zw, zd, 2.6 * this.scale);
    g.fillStyle = owner ? hexA(owner.color, mine ? 0.16 : 0.08) : "rgba(255,255,255,0.04)";
    g.fill();
    g.setLineDash([6, 5]);
    g.strokeStyle = owner ? hexA(owner.color, 0.65) : "rgba(255,255,255,0.18)";
    g.lineWidth = mine ? 2 : 1.2;
    g.stroke();
    g.setLineDash([]);
    // Label sits just OFF the table, behind the mat (local +z = outward).
    g.fillStyle = owner ? hexA(owner.color, 0.75) : "rgba(255,255,255,0.25)";
    g.font = `600 ${Math.max(9, 1.9 * this.scale)}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText(owner ? `${owner.name}${mine ? " (you)" : ""}` : "open seat", 0, zd / 2 + Math.max(12, 2.6 * this.scale));
    g.restore();
  }

  private drawObject(o: RenderObj, hovered: boolean, state: Replica): void {
    const g = this.g;
    const d = getDef(o.def);
    const [sx, sy] = this.toScreen(o.rp.x, o.rp.z);
    const lift = Math.max(0, o.rp.y - d.size[1] / 2); // height above table
    const liftScale = 1 + Math.min(lift * 0.02, 0.1);
    const pop = 0.85 + 0.15 * easeOut(o.spawnT);
    const s = this.scale * liftScale * pop;
    const jiggle = o.fxShuffle > 0 ? Math.sin(this.time * 55) * o.fxShuffle * 0.12 : 0;

    // Shadow.
    if (d.kind !== "board") {
      g.save();
      g.translate(sx + lift * 0.55 * this.scale * 0.18 + 2, sy + lift * 0.55 * this.scale * 0.18 + 3);
      g.rotate(o.ryaw);
      g.fillStyle = `rgba(0,0,0,${0.28 - Math.min(lift * 0.02, 0.14)})`;
      if (d.shape === "cylinder") {
        g.beginPath();
        g.ellipse(0, 0, (d.size[0] / 2) * s, (d.size[2] / 2) * s, 0, 0, Math.PI * 2);
        g.fill();
      } else {
        this.rr(-(d.size[0] / 2) * s, -(d.size[2] / 2) * s, d.size[0] * s, d.size[2] * s, d.kind === "card" || d.kind === "stack" ? 0.6 * s : 0.2 * s);
        g.fill();
      }
      g.restore();
    }

    g.save();
    g.translate(sx, sy);
    g.rotate(o.ryaw + jiggle);

    switch (d.kind) {
      case "board": {
        const bw = d.size[0] * s, bd = d.size[2] * s;
        if (d.front) g.drawImage(getFace(d.front, d.size[0], d.size[2]), -bw / 2, -bd / 2, bw, bd);
        break;
      }
      case "card":
        this.drawCard(o, d.size[0], d.size[2], s, o.flipT);
        break;
      case "stack": {
        if (d.acceptsGroup?.startsWith("cards")) {
          // Deck: offset card-edge layers with separation lines so the stack
          // visibly reads as individual cards.
          const count = o.count ?? 1;
          const layers = Math.min(7, count - 1);
          const r = (d.cornerRadius ?? 0.6) * s;
          for (let i = layers; i > 0; i--) {
            const off = i * Math.max(1, 0.16 * s);
            g.fillStyle = i % 2 ? "#cfc9ba" : "#e8e2d2";
            this.rr(-(d.size[0] / 2) * s + off, -(d.size[2] / 2) * s + off, d.size[0] * s, d.size[2] * s, r);
            g.fill();
            g.strokeStyle = "rgba(60,50,40,0.35)";
            g.lineWidth = 1;
            g.stroke();
          }
          this.drawCard(o, d.size[0], d.size[2], s, o.flipT, o.topDef);
          this.badge(count, (d.size[0] / 2) * s, -(d.size[2] / 2) * s);
        } else {
          // Chip stack: arc slivers along the lower rim, one per few chips.
          const count = o.count ?? 1;
          const R = (d.size[0] / 2) * s;
          const slivers = Math.min(6, count - 1);
          for (let i = slivers; i > 0; i--) {
            const off = i * Math.max(1, 0.12 * s);
            g.beginPath();
            g.ellipse(off * 0.4, off * 0.4, R, R, 0, 0, Math.PI * 2);
            g.fillStyle = i % 2 ? shade(d.color ?? "#888", -28) : shade(d.color ?? "#888", -12);
            g.fill();
            g.strokeStyle = "rgba(0,0,0,0.3)";
            g.lineWidth = 1;
            g.stroke();
          }
          this.drawToken(o, d, s);
          this.badge(count, (d.size[0] / 2) * s * 0.8, -(d.size[2] / 2) * s * 0.8);
        }
        break;
      }
      case "token":
        this.drawToken(o, d, s);
        break;
      case "die": {
        const sides = d.sides ?? 6;
        let value = o.value ?? sides;
        if (o.fxRoll > 0) {
          // Tumbling: scramble the shown face and wobble until the fx ends.
          const hash = o.id.charCodeAt(o.id.length - 1);
          value = 1 + ((Math.floor(this.time * 22) + hash) % sides);
          g.rotate(Math.sin(this.time * 31 + hash) * o.fxRoll * 0.9);
        }
        const face = getFace(
          { type: "proc-die", sides, value, color: d.color ?? "#888888", label: d.labels?.[value - 1] },
          d.size[0], d.size[2],
        );
        const dw = d.size[0] * s, dd = d.size[2] * s;
        g.drawImage(face, -dw / 2, -dd / 2, dw, dd);
        break;
      }
      case "zone":
        break;
    }

    // MTG counters: badge column pinned to the card's top-right corner.
    if (o.counters?.length && (d.kind === "card" || d.kind === "stack")) {
      const badge = getCounterBadges(o.counters);
      const bw = Math.max(26, 3.4 * this.scale);
      const bh = bw * (badge.height / badge.width);
      g.drawImage(badge, (d.size[0] / 2) * s - bw * 0.6, -(d.size[2] / 2) * s - 4, bw, bh);
    }

    // Hover / held / selection outline: follows the card's actual corner
    // radius, stood off a few px so it never covers the art.
    const heldByOther = o.heldBy && o.heldBy !== state.you;
    const isSelected = state.selected.has(o.id);
    // Locked scenery (boards, mats) shouldn't flash under the pointer.
    if ((hovered && !o.locked) || heldByOther || isSelected) {
      const holder = heldByOther ? state.players.find((p) => p.id === o.heldBy) : null;
      g.strokeStyle = holder ? holder.color : isSelected ? "#4da3ff" : "rgba(255,255,255,0.9)";
      g.lineWidth = isSelected ? 3.5 : 2;
      const off = isSelected ? 4 : 3;
      if (d.shape === "cylinder") {
        g.beginPath();
        g.ellipse(0, 0, (d.size[0] / 2) * s + off, (d.size[2] / 2) * s + off, 0, 0, Math.PI * 2);
        g.stroke();
      } else {
        const r = (d.cornerRadius ?? 0.4) * s + off;
        this.rr(-(d.size[0] / 2) * s - off, -(d.size[2] / 2) * s - off, d.size[0] * s + off * 2, d.size[2] * s + off * 2, r);
        g.stroke();
      }
    }
    if (o.locked && hovered && d.kind !== "board") {
      g.fillStyle = "rgba(255,255,255,0.85)";
      g.font = `${1.8 * this.scale}px system-ui`;
      g.textAlign = "center";
      g.fillText("🔒", 0, -(d.size[2] / 2) * s - 6);
    }
    g.restore();
  }

  /** Card with horizontal squash flip animation. flipT 1=front, 0=back. */
  private drawCard(o: RenderObj, wu: number, du: number, s: number, flipT: number, topDef?: string): void {
    const g = this.g;
    const d = getDef(o.def);
    const squash = Math.abs(flipT * 2 - 1); // 1 at rest, 0 mid-flip
    const showFront = flipT > 0.5;
    let spec = showFront ? (getDef(topDef ?? o.def).front ?? d.front) : d.back;
    if (!spec) spec = { type: "color", color: "#999" };
    const face = getFace(spec, wu, du);
    const cw = wu * s * Math.max(squash, 0.06), cd = du * s;
    g.save();
    const r = d.cornerRadius ?? 0;
    if (r > 0) {
      // Clip to the def's corner radius (matters for imported image faces).
      this.rr(-cw / 2, -cd / 2, cw, cd, r * s * Math.max(squash, 0.06));
      g.clip();
    }
    g.drawImage(face, -cw / 2, -cd / 2, cw, cd);
    g.restore();
  }

  private drawToken(o: RenderObj, d: ReturnType<typeof getDef>, s: number): void {
    const g = this.g;
    const R = (d.size[0] / 2) * s;
    // Chess glyphs stay upright in top-down view whatever the piece faces.
    if (d.piece) g.rotate(-o.ryaw);
    if (d.front) {
      const face = getFace(d.front, d.size[0], d.size[2]);
      g.save();
      g.beginPath();
      g.ellipse(0, 0, R, (d.size[2] / 2) * s, 0, 0, Math.PI * 2);
      g.clip();
      g.drawImage(face, -R, -(d.size[2] / 2) * s, R * 2, d.size[2] * s);
      g.restore();
    } else {
      // Pawn-style token: filled disc + highlight.
      g.beginPath();
      g.ellipse(0, 0, R, R, 0, 0, Math.PI * 2);
      g.fillStyle = d.color ?? "#888";
      g.fill();
      g.strokeStyle = "rgba(0,0,0,0.35)";
      g.lineWidth = 1.5;
      g.stroke();
      g.beginPath();
      g.ellipse(-R * 0.25, -R * 0.25, R * 0.32, R * 0.32, 0, 0, Math.PI * 2);
      g.fillStyle = "rgba(255,255,255,0.35)";
      g.fill();
    }
  }

  private badge(n: number, x: number, y: number): void {
    const g = this.g;
    const r = Math.max(8, 1.35 * this.scale);
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fillStyle = "rgba(18,18,22,0.88)";
    g.fill();
    g.strokeStyle = "rgba(255,255,255,0.35)";
    g.lineWidth = 1;
    g.stroke();
    g.fillStyle = "#f5f2ea";
    g.font = `bold ${r * (String(n).length > 2 ? 0.78 : 1.0)}px system-ui, sans-serif`;
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText(String(n), x, y + 0.5);
  }
}

function hexA(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** Lighten (+) or darken (−) a hex color by an absolute channel amount. */
function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = (v: number) => Math.max(0, Math.min(255, v + amt));
  return `rgb(${c((n >> 16) & 255)},${c((n >> 8) & 255)},${c(n & 255)})`;
}

function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}
